/**
 * The permission catalogue every service shares.
 *
 * Permissions follow the dashboard's sidebar: each screen has a View key and
 * keys for what can be done on it. This file holds the keys, the View each
 * action needs, what keys stored before this layout now grant, the built-in
 * roles' defaults, and the compact form auth signs into the access token.
 *
 * It is copied verbatim into auth, the dashboard API, products,
 * receivepayment, sendpayment, pos-backend and esl. A key's position in
 * PERMISSION_KEYS is its bit in the token, so the list is append-only: add new
 * keys at the end, never reorder or remove one. Every copy's tests pin
 * PERMISSION_KEYS_FINGERPRINT.
 */

export const PERMISSION_KEYS = [
  // Users
  'users.view',
  'users.create',
  'users.edit',
  'users.deactivate',
  // Receive Payments
  'rp.sale.create',
  'rp.sale.schedule',
  'rp.links.view',
  'rp.links.create',
  'rp.recurring.view',
  'rp.recurring.edit',
  'rp.recurring.cancel',
  'rp.invoices.view',
  'rp.invoices.create',
  'rp.invoices.edit',
  'rp.invoices.delete',
  'rp.invoices.send',
  'rp.invoices.void',
  'rp.invoices.payment',
  'rp.customers.view',
  'rp.customers.create',
  'rp.customers.edit',
  'rp.customers.delete',
  'rp.transactions.view',
  'rp.transactions.refund',
  'rp.transactions.cancel',
  'rp.transactions.resubmit',
  'rp.funding.view',
  // POS Management: catalog
  'products.view',
  'products.add',
  'products.edit',
  'products.delete',
  'products.prices',
  'products.barcodes',
  'products.vendors',
  'vendors.add',
  'products.import',
  'products.export',
  'departments.view',
  'departments.add',
  'departments.edit',
  'departments.delete',
  'categories.view',
  'categories.add',
  'categories.edit',
  'categories.delete',
  'modifiers.view',
  'modifiers.add',
  'modifiers.edit',
  'modifiers.delete',
  'promotions.view',
  'promotions.create',
  'promotions.edit',
  'promotions.delete',
  'favorites.view',
  'favorites.edit',
  // POS Management: orders and stock
  'purchase_orders.view',
  'purchase_orders.create',
  'purchase_orders.edit',
  'purchase_orders.delete',
  'purchase_orders.send',
  'purchase_orders.receive',
  'purchase_orders.cancel',
  'sales_orders.view',
  'sales_orders.edit',
  'sales_orders.fulfil',
  'sales_orders.packing',
  // POS Management: customer management
  'customers.view',
  'customers.create',
  'customers.edit',
  'coupons.view',
  'coupons.create',
  'coupons.deactivate',
  'rewards.view',
  'rewards.create',
  'rewards.deactivate',
  'campaigns.view',
  'campaigns.create',
  'campaigns.delete',
  'campaigns.send',
  'campaigns.cancel',
  // Send Payments
  'sp.payees.view',
  'sp.payees.create',
  'sp.payees.edit',
  'sp.payees.delete',
  'sp.payments.send',
  'sp.recurring.view',
  'sp.recurring.create',
  'sp.recurring.edit',
  'sp.recurring.cancel',
  'sp.invoices.view',
  'sp.invoices.upload',
  'sp.invoices.edit',
  'sp.invoices.approve',
  'sp.invoices.inventory',
  'sp.invoices.autopay',
  'sp.bills.view',
  'sp.bills.pay',
  'sp.transactions.view',
  // Reports
  'reports.rp.view',
  'reports.rp.export',
  'reports.pos.view',
  'reports.pos.export',
  'reports.pos.refunded',
  'reports.pos.credit',
  'reports.sp.view',
  'reports.sp.export',
  // Transactions, Reconciliation, Statements
  'transactions.view',
  'reconciliation.card.view',
  'reconciliation.upload',
  'reconciliation.cash.view',
  'reconciliation.cash.edit',
  'statements.view',
  // Shelf Labels
  'esl.labels.view',
  'esl.labels.create',
  'esl.labels.edit',
  'esl.labels.export',
  'esl.gateways.view',
  'esl.gateways.create',
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

/** `PERMISSION_KEYS.join(',')` hashed with SHA-256. Every copy's tests check it. */
export const PERMISSION_KEYS_FINGERPRINT = '9c53e0d15be6c7862c2c51326f4346d4a799927a42effece4f95bd6bc8f323c7';

const KNOWN = new Set<string>(PERMISSION_KEYS);
export const isPermissionKey = (key: unknown): key is PermissionKey => typeof key === 'string' && KNOWN.has(key);

/**
 * Screens that have a View key, each with the actions that need it. Holding an
 * action means holding its screen's View. Screens without a View (New Sale,
 * Send Payment) aren't listed.
 */
const SCREENS: readonly (readonly PermissionKey[])[] = [
  ['users.view', 'users.create', 'users.edit', 'users.deactivate'],
  ['rp.links.view', 'rp.links.create'],
  ['rp.recurring.view', 'rp.recurring.edit', 'rp.recurring.cancel'],
  [
    'rp.invoices.view',
    'rp.invoices.create',
    'rp.invoices.edit',
    'rp.invoices.delete',
    'rp.invoices.send',
    'rp.invoices.void',
    'rp.invoices.payment',
  ],
  ['rp.customers.view', 'rp.customers.create', 'rp.customers.edit', 'rp.customers.delete'],
  ['rp.transactions.view', 'rp.transactions.refund', 'rp.transactions.cancel', 'rp.transactions.resubmit'],
  [
    'products.view',
    'products.add',
    'products.edit',
    'products.delete',
    'products.prices',
    'products.barcodes',
    'products.vendors',
    'vendors.add',
    'products.import',
    'products.export',
  ],
  ['departments.view', 'departments.add', 'departments.edit', 'departments.delete'],
  ['categories.view', 'categories.add', 'categories.edit', 'categories.delete'],
  ['modifiers.view', 'modifiers.add', 'modifiers.edit', 'modifiers.delete'],
  ['promotions.view', 'promotions.create', 'promotions.edit', 'promotions.delete'],
  ['favorites.view', 'favorites.edit'],
  [
    'purchase_orders.view',
    'purchase_orders.create',
    'purchase_orders.edit',
    'purchase_orders.delete',
    'purchase_orders.send',
    'purchase_orders.receive',
    'purchase_orders.cancel',
  ],
  ['sales_orders.view', 'sales_orders.edit', 'sales_orders.fulfil', 'sales_orders.packing'],
  ['customers.view', 'customers.create', 'customers.edit'],
  ['coupons.view', 'coupons.create', 'coupons.deactivate'],
  ['rewards.view', 'rewards.create', 'rewards.deactivate'],
  ['campaigns.view', 'campaigns.create', 'campaigns.delete', 'campaigns.send', 'campaigns.cancel'],
  ['sp.payees.view', 'sp.payees.create', 'sp.payees.edit', 'sp.payees.delete'],
  ['sp.recurring.view', 'sp.recurring.create', 'sp.recurring.edit', 'sp.recurring.cancel'],
  [
    'sp.invoices.view',
    'sp.invoices.upload',
    'sp.invoices.edit',
    'sp.invoices.approve',
    'sp.invoices.inventory',
    'sp.invoices.autopay',
  ],
  ['sp.bills.view', 'sp.bills.pay'],
  ['reports.rp.view', 'reports.rp.export'],
  ['reports.pos.view', 'reports.pos.export', 'reports.pos.refunded', 'reports.pos.credit'],
  ['reports.sp.view', 'reports.sp.export'],
  ['reconciliation.card.view', 'reconciliation.upload'],
  ['reconciliation.cash.view', 'reconciliation.cash.edit'],
  ['esl.labels.view', 'esl.labels.create', 'esl.labels.edit', 'esl.labels.export'],
  ['esl.gateways.view', 'esl.gateways.create'],
];

/** The View an action needs, for every action on a screen that has one. */
export const VIEW_FOR: Readonly<Partial<Record<PermissionKey, PermissionKey>>> = Object.fromEntries(
  SCREENS.flatMap(([view, ...actions]) => actions.map((action) => [action, view])),
);

/** Everything under POS Management's Catalog that used to be the single `products` permission. */
export const CATALOGUE_KEYS: readonly PermissionKey[] = SCREENS.filter(([view]) =>
  ['products.view', 'departments.view', 'categories.view', 'modifiers.view'].includes(view),
).flat();

const startsWith = (...prefixes: string[]) => PERMISSION_KEYS.filter((k) => prefixes.some((p) => k.startsWith(p)));

const SEND_PAYMENTS: readonly PermissionKey[] = [
  ...startsWith('sp.payees.'),
  'sp.invoices.view',
  'sp.invoices.upload',
  'sp.invoices.edit',
  'sp.invoices.approve',
  'sp.invoices.inventory',
  'sp.bills.view',
  'sp.transactions.view',
  'transactions.view',
];

/**
 * Keys stored or signed before permissions followed the sidebar, and what each
 * grants now. They are accepted wherever a permission list is read.
 */
export const LEGACY_KEYS: Readonly<Record<string, readonly PermissionKey[]>> = {
  products: CATALOGUE_KEYS,
  promotions: startsWith('promotions.'),
  favorites: startsWith('favorites.'),
  // Fulfilling a warehouse order sat under purchase orders.
  purchase_orders: [
    'purchase_orders.view',
    'purchase_orders.create',
    'purchase_orders.edit',
    'purchase_orders.send',
    'purchase_orders.receive',
    'purchase_orders.cancel',
    'sales_orders.view',
    'sales_orders.fulfil',
  ],
  sales_orders: startsWith('sales_orders.'),
  customers: startsWith('customers.', 'coupons.', 'rewards.', 'campaigns.'),
  receive_payments: [
    'rp.sale.create',
    'rp.sale.schedule',
    ...startsWith('rp.links.', 'rp.recurring.', 'rp.invoices.'),
    'rp.customers.view',
    'rp.customers.create',
    'rp.customers.edit',
    'rp.transactions.view',
    'rp.transactions.resubmit',
    'transactions.view',
    'reconciliation.card.view',
    'reconciliation.cash.view',
    'reconciliation.cash.edit',
  ],
  send_payments: SEND_PAYMENTS,
  send_payments_pay: [
    ...SEND_PAYMENTS,
    'sp.payments.send',
    ...startsWith('sp.recurring.'),
    'sp.invoices.autopay',
    'sp.bills.pay',
  ],
  reports: startsWith('reports.'),
};

/** Built-in role levels, for the screens that stay Owner or Admin only. */
export const ROLE_LEVEL: Readonly<Record<string, number>> = {
  super_admin: 5,
  owner: 4,
  admin: 3,
  manager: 2,
  employee: 1,
  inventory_manager: 1,
};

/** Adds the View every held action needs. */
function withViews(keys: Set<PermissionKey>): PermissionKey[] {
  for (const key of [...keys]) {
    const view = VIEW_FOR[key];
    if (view) keys.add(view);
  }
  return PERMISSION_KEYS.filter((k) => keys.has(k));
}

/**
 * Reads a permission list: keeps known keys, swaps legacy keys for what they
 * grant, drops anything else, and adds the View each action needs. Returns the
 * keys in catalogue order.
 */
export function expandPermissions(keys: Iterable<unknown>): PermissionKey[] {
  const out = new Set<PermissionKey>();
  for (const key of keys) {
    if (typeof key !== 'string') continue;
    const legacy = Object.prototype.hasOwnProperty.call(LEGACY_KEYS, key) ? LEGACY_KEYS[key] : undefined;
    if (legacy) legacy.forEach((k) => out.add(k));
    else if (KNOWN.has(key)) out.add(key as PermissionKey);
  }
  return withViews(out);
}

/**
 * Converts a permission list written before this layout, for a holder of
 * `role`, into exactly what it let them do: no more, no less. Beyond
 * `expandPermissions`: View products used to cover the whole catalogue (and
 * anyone who saw it could export it), Reports and Customer Management also
 * needed the Admin role, and some screens were opened by role alone.
 * Used once for stored roles and extras, and for access tokens signed before
 * the switch.
 */
export function upgradeLegacyPermissions(keys: readonly unknown[], role: string): PermissionKey[] {
  const level = ROLE_LEVEL[role] ?? 0;
  const held = new Set(keys.filter((k): k is string => typeof k === 'string'));
  const usable = [...held].filter((k) => level >= ROLE_LEVEL.admin || (k !== 'reports' && k !== 'customers'));
  const out = new Set<PermissionKey>(expandPermissions(usable));
  const add = (...ks: PermissionKey[]) => ks.forEach((k) => out.add(k));
  if (CATALOGUE_KEYS.some((k) => out.has(k))) {
    add('products.view', 'departments.view', 'categories.view', 'modifiers.view', 'products.export');
  }
  const receives = held.has('receive_payments');
  if (level >= ROLE_LEVEL.manager) {
    // Registering label gateways was Admin only.
    add(...startsWith('esl.').filter((k) => k !== 'esl.gateways.create'));
    if (receives) add('rp.transactions.refund', 'rp.transactions.cancel', 'reconciliation.upload');
  }
  if (level >= ROLE_LEVEL.admin) {
    add(...startsWith('users.'), 'statements.view', 'esl.gateways.create');
    if (receives) add('rp.funding.view', 'rp.customers.delete');
    if (held.has('purchase_orders')) add('purchase_orders.delete');
  }
  return withViews(out);
}

const ALL: readonly PermissionKey[] = PERMISSION_KEYS;
const except = (drop: readonly PermissionKey[]) => ALL.filter((k) => !drop.includes(k));

/** Cashier: adds and edits the catalogue, and arranges favorites. */
const CASHIER: readonly PermissionKey[] = [
  'products.view',
  'products.add',
  'products.edit',
  'products.prices',
  'products.barcodes',
  'products.vendors',
  'vendors.add',
  'products.export',
  'departments.view',
  'departments.add',
  'departments.edit',
  'categories.view',
  'categories.add',
  'categories.edit',
  'modifiers.view',
  'modifiers.add',
  'modifiers.edit',
  'favorites.view',
  'favorites.edit',
];

/**
 * What each built-in role can do. These match what each role could actually
 * open before permissions followed the sidebar. A custom role replaces them
 * for its holders.
 */
export const ROLE_PERMISSIONS: Readonly<Record<string, readonly PermissionKey[]>> = {
  owner: ALL,
  admin: ALL,
  manager: except([
    ...startsWith('users.', 'customers.', 'coupons.', 'rewards.', 'campaigns.', 'reports.', 'sp.recurring.'),
    'rp.funding.view',
    'rp.customers.delete',
    'sp.payments.send',
    'sp.bills.pay',
    'sp.invoices.autopay',
    'products.import',
    'departments.delete',
    'categories.delete',
    'modifiers.delete',
    'purchase_orders.delete',
    'statements.view',
    'esl.gateways.create',
  ]),
  employee: CASHIER,
  inventory_manager: [
    ...CASHIER,
    'products.delete',
    ...startsWith('promotions.'),
    'purchase_orders.view',
    'purchase_orders.create',
    'purchase_orders.edit',
    'purchase_orders.send',
    'purchase_orders.receive',
    'purchase_orders.cancel',
    ...startsWith('sales_orders.'),
  ],
};

/**
 * A holder's full set: their custom role's permissions when they hold one,
 * otherwise their built-in role's defaults, plus any extras.
 */
export function permissionsFor(
  role: string,
  customRolePermissions: readonly unknown[] | null | undefined,
  extras: readonly unknown[] | null | undefined,
): PermissionKey[] {
  return expandPermissions([...(customRolePermissions ?? ROLE_PERMISSIONS[role] ?? []), ...(extras ?? [])]);
}

const BASE64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/**
 * The compact form auth signs into the access token as `pb`: one bit per key
 * in PERMISSION_KEYS order, six bits to a base64url character.
 */
export function encodePermissionBits(keys: Iterable<string>): string {
  const held = new Set(keys);
  let out = '';
  for (let i = 0; i < PERMISSION_KEYS.length; i += 6) {
    let value = 0;
    for (let bit = 0; bit < 6 && i + bit < PERMISSION_KEYS.length; bit++) {
      if (held.has(PERMISSION_KEYS[i + bit])) value |= 1 << bit;
    }
    out += BASE64URL[value];
  }
  return out;
}

/**
 * Reads `pb`. Bits past the end of this copy's list (keys a newer auth knows)
 * are ignored. Returns null when it isn't a valid string.
 */
export function decodePermissionBits(bits: unknown): PermissionKey[] | null {
  if (typeof bits !== 'string') return null;
  const out: PermissionKey[] = [];
  for (let c = 0; c < bits.length; c++) {
    const value = BASE64URL.indexOf(bits[c]);
    if (value < 0) return null;
    for (let bit = 0; bit < 6; bit++) {
      const key = PERMISSION_KEYS[c * 6 + bit];
      if (key && value & (1 << bit)) out.push(key);
    }
  }
  return out;
}

/**
 * The permissions an access token carries: `pb` from current auth, or the
 * `permissions` list from tokens signed before the switch, upgraded for the
 * token's role. Null when it carries neither (super admins, devices).
 */
export function readTokenPermissions(payload: {
  pb?: unknown;
  permissions?: unknown;
  role?: unknown;
}): PermissionKey[] | null {
  if (payload.pb !== undefined) return decodePermissionBits(payload.pb);
  if (Array.isArray(payload.permissions)) {
    return upgradeLegacyPermissions(payload.permissions, typeof payload.role === 'string' ? payload.role : '');
  }
  return null;
}
